/**
 * SOLOMON MESSAGE BUBBLE
 * Hermès luxury chat message styling.
 *
 * Day 4: Mode badge + switch dropdown
 * Day 5: Tri-model indicator badge (Claude/GPT/Gemini) + confidence flag
 *
 * User messages:    right-aligned, orange/copper gradient
 * Solomon messages: left-aligned, glass/charcoal surface with model-colored accent
 */

import { useState, useRef, useEffect, Fragment, memo, lazy, Suspense } from 'react';
import { useSolomon } from '../../context/SolomonContext';
import katex from 'katex';
import 'katex/dist/katex.min.css';
import DiagramBlock from './DiagramBlock';
import GeneratedImage from './GeneratedImage';
import { extractImageVisuals } from './imageTokens.js';
import { ActionCard } from '../ActionCard';
import MessageSources from './MessageSources';
import FileCard from './FileCard';
import {
  containsLatexCommands,
  latexToPlainText,
  DOLLAR_LATEX_SPAN_SOURCE,
  containsLatexCommand,
} from '../../utils/latexNormalize';
import { getModelConfig, FLAG_CONFIG, REGENERATE_MODELS, formatModelName } from './modelBadgeConfig';
import { summarizeSteps, stepLineText } from './stepsSummary.js';
import './chat.css';

// Recharts is ~230KB gzip — far past the +25–40KB the swap approval accepted
// as an EAGER cost — so the chart stack loads only when a message actually
// carries a ```chart block. Chart-less sessions never fetch it, and the
// initial bundle is smaller than the Chart.js era's. The wrapper keeps the
// two call sites' <ChartBlock config={…}/> shape unchanged.
const LazyChartBlock = lazy(() => import('./ChartBlock'));
function ChartBlock(props) {
  return (
    <Suspense fallback={<div className="message-visual-pending">Rendering chart…</div>}>
      <LazyChartBlock {...props} />
    </Suspense>
  );
}

// ============================================================
// KATEX MATH HELPERS
// ============================================================
//
// Delimiters supported (intentionally NOT bare `$...$`):
//   $$...$$  → block math   \[...\]  → block math   \(...\)  → inline math
// Bare single `$` is ALWAYS literal text — the operator's content is
// finance-heavy ("$5,000 to $10,000") and bare-$ detection would mangle
// currency. Every render is wrapped in try/catch with throwOnError:false so
// a bad formula degrades to the raw tex string instead of crashing the bubble.

function renderMathToHtml(tex, displayMode) {
  try {
    return katex.renderToString(tex, { displayMode, throwOnError: false });
  } catch {
    // throwOnError:false should already prevent throws, but double-guard:
    // fall back to the raw tex so a malformed formula never crashes render.
    return null;
  }
}

// ============================================================
// MODE CONFIG
// ============================================================

const MODES = [
  { id: 'conversation', label: 'Conversation', icon: '◯', description: 'Natural dialogue' },
  { id: 'command',      label: 'Command',      icon: '⌘', description: 'Fast & direct' },
  { id: 'research',     label: 'Research',     icon: '◎', description: 'Deep & thorough' },
  { id: 'creative',     label: 'Creative',     icon: '✦', description: 'Ideas & imagination' },
  { id: 'analysis',     label: 'Analysis',     icon: '◈', description: 'Data & numbers' },
];

function getModeConfig(modeId) {
  return MODES.find(m => m.id === modeId) || MODES[0];
}

// getModelConfig / FLAG_CONFIG / REGENERATE_MODELS live in ./modelBadgeConfig.js
// (Task 12 relocated; Task 18 re-tokenized to the design-system badge spec — one
// token per provider, var() refs, no raw hex). The MODEL_CONFIG ID map it used to
// export is gone (2026-08-07) — provider identity resolves by family in
// src/lib/modelRegistry.js, shared with ConversationSidebar and ActionCard.

// ============================================================
// THINKING INDICATOR
// ============================================================

export function ThinkingIndicator({ modelHint = null, inline = false }) {
  // Cycle through routing phases to show Solomon is working
  const [phase, setPhase] = useState(0);
  const PHASES = modelHint
    ? [`Routing to ${modelHint}…`, `${modelHint} is thinking…`, `${modelHint} is thinking…`]
    : ['Routing…', 'Thinking…', 'Thinking…'];

  useEffect(() => {
    // Move through phases: 0 → 1 after 1.2s, stay at 1 after that
    if (phase >= PHASES.length - 1) return;
    const t = setTimeout(() => setPhase(p => Math.min(p + 1, PHASES.length - 1)), 1200);
    return () => clearTimeout(t);
  }, [phase, PHASES.length]);

  const bar = (
    <div className={inline ? 'thinking-inline' : 'thinking-bubble'}>
      <span className="sol-filament" aria-hidden="true" />
      <span className="thinking-phase-label">
        {PHASES[phase]}
      </span>
    </div>
  );

  // Inline mode renders ONLY the filament + label — the merged streaming
  // card (MessageBubble) already provides the message-row/avatar shell, so
  // a second one here would nest a bubble inside a bubble.
  if (inline) return bar;

  return (
    <div className="message-row message-row--solomon">
      <div className="message-avatar message-avatar--solomon">S</div>
      {bar}
    </div>
  );
}

// ============================================================
// MODE BADGE + SWITCH DROPDOWN
// ============================================================

function ModeBadge({ detectedMode, userOverride, onSwitch, isLastMessage }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab]   = useState('model'); // 'model' or 'mode'
  const dropdownRef = useRef(null);
  const modeConfig = getModeConfig(detectedMode);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleModeSwitch = (modeId) => {
    setOpen(false);
    onSwitch(modeId);
  };

  const handleModelSwitch = (modelId) => {
    setOpen(false);
    // Pass current mode + model override
    onSwitch(detectedMode || 'auto', modelId);
  };

  return (
    <div className="mode-badge-container" ref={dropdownRef}>
      <div className="mode-badge">
        <span className="mode-badge-icon">{modeConfig.icon}</span>
        <span className="mode-badge-label">{modeConfig.label}</span>
        {userOverride && <span className="mode-badge-override" title="You switched this">↺</span>}
      </div>

      {isLastMessage && (
        <button
          className="mode-switch-btn"
          onClick={() => setOpen(o => !o)}
          title="Regenerate with different mode or model"
        >
          Regenerate ↕
        </button>
      )}

      {open && (
        <div className="mode-switch-dropdown">
          {/* Tab selector */}
          <div className="regen-tabs">
            <button
              className={`regen-tab ${tab === 'model' ? 'regen-tab--active' : ''}`}
              onClick={() => setTab('model')}
            >
              Model
            </button>
            <button
              className={`regen-tab ${tab === 'mode' ? 'regen-tab--active' : ''}`}
              onClick={() => setTab('mode')}
            >
              Mode
            </button>
          </div>

          {tab === 'model' && (
            <>
              <div className="mode-switch-label">Regenerate with:</div>
              {REGENERATE_MODELS.map(m => (
                <button
                  key={m.id}
                  className="mode-switch-option"
                  onClick={() => handleModelSwitch(m.id)}
                >
                  <span className="mode-switch-option-icon" style={{ color: m.color }}>{m.icon}</span>
                  <span className="mode-switch-option-name">{m.label}</span>
                  <span className="mode-switch-option-desc">{m.description}</span>
                </button>
              ))}
            </>
          )}

          {tab === 'mode' && (
            <>
              <div className="mode-switch-label">Regenerate in mode:</div>
              {MODES.filter(m => m.id !== detectedMode).map(mode => (
                <button
                  key={mode.id}
                  className="mode-switch-option"
                  onClick={() => handleModeSwitch(mode.id)}
                >
                  <span className="mode-switch-option-icon">{mode.icon}</span>
                  <span className="mode-switch-option-name">{mode.label}</span>
                  <span className="mode-switch-option-desc">{mode.description}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MODEL INDICATOR BADGE (Day 5)
// ============================================================

function ModelIndicatorBadge({ model, confidence, routingReason }) {
  const config = getModelConfig(model);
  if (!config || model === 'error') return null;

  // Group 1 (2026-09-14): provider logo (glyph when the set has no mark, e.g.
  // GPT) + derived model name so the operator sees which part of Solomon
  // answered. Raw id stays in the tooltip.
  const name = formatModelName(model) || model;
  const fellBack = typeof routingReason === 'string' && routingReason.startsWith('Fallback');
  const title = [model, confidence ? `${Math.round(confidence)}% confidence` : null, routingReason]
    .filter(Boolean).join(' · ');

  // Confidence bands per design-system.md: ≥80 success · 60-79 warning · <60 error.
  const band = confidence == null ? null
    : confidence >= 80 ? 'success'
    : confidence >= 60 ? 'warning'
    : 'error';

  return (
    <>
      <span className={`sol-badge sol-badge--${config.variant} model-indicator-badge`} title={title}>
        {config.logo
          ? <svg className="model-indicator-logo" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={config.logo} /></svg>
          : <span className="model-indicator-icon" aria-hidden="true">{config.icon}</span>}
        {name}
        {fellBack && (
          <span className="model-indicator-fallback" title="served by fallback" role="img" aria-label="served by fallback">↷</span>
        )}
      </span>
      {band && (
        <span
          className={`sol-badge sol-badge--${band}`}
          title={`${Math.round(confidence)}% routing confidence`}
        >
          {Math.round(confidence)}%
        </span>
      )}
    </>
  );
}

// ============================================================
// CONFIDENCE FLAG (Day 5)
// ============================================================

function ConfidenceFlag({ flag }) {
  if (!flag || !FLAG_CONFIG[flag]) return null;
  const config = FLAG_CONFIG[flag];

  return (
    <span
      className="confidence-flag"
      style={{ '--flag-color': config.color }}
      title={config.title}
    >
      {config.symbol}
    </span>
  );
}

// ============================================================
// MESSAGE BUBBLE
// ============================================================

// ============================================================
// BRIEFING BUBBLE — special morning briefing rendering
// ============================================================

function BriefingBubble({ content, timestamp, briefingType = 'morning' }) {
  const isEvening = briefingType === 'evening';

  const formatTime = (ts) => {
    if (!ts) return '';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const todayLabel = new Date().toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });

  // Split flowing-prose briefing into paragraph sections so each one can
  // be rendered with a subtle divider between.
  const sections = (content || '')
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <div className="message-row message-row--solomon animate-fade-in-up">
      <div className="message-avatar message-avatar--solomon">S</div>
      <article className={`briefing-card ${isEvening ? 'briefing-card--evening' : ''}`}>
        <header className="briefing-card-header">
          <div className="briefing-card-eyebrow">
            <span className="briefing-card-sun" aria-hidden="true">{isEvening ? '🌙' : '☀'}</span>
            <span className="briefing-card-eyebrow-text">{isEvening ? 'Evening Wrap-Up' : 'Morning Briefing'}</span>
          </div>
          <h2 className="briefing-card-title">{isEvening ? 'Good evening, Justin' : 'Good morning, Justin'}</h2>
          <p className="briefing-card-date">{todayLabel}</p>
        </header>

        <div className="briefing-card-divider briefing-card-divider--strong" />

        <div className="briefing-card-body">
          {sections.length > 0 ? (
            sections.map((section, i) => (
              <Fragment key={`brf-${i}`}>
                <div className="briefing-card-section">
                  {formatContent(section)}
                </div>
                {i < sections.length - 1 && (
                  <div className="briefing-card-divider briefing-card-divider--soft" />
                )}
              </Fragment>
            ))
          ) : (
            <div className="briefing-card-section">{formatContent(content)}</div>
          )}
        </div>

        <div className="briefing-card-divider briefing-card-divider--strong" />

        <footer className="briefing-card-footer">
          <span className="briefing-card-signature">— Solomon</span>
          {timestamp && (
            <span className="briefing-card-time">{formatTime(timestamp)}</span>
          )}
        </footer>
      </article>
    </div>
  );
}

// ============================================================
// MESSAGE BUBBLE
// ============================================================

// Output-quality rating control — 4 one-tap targets under each Solomon response.
// 0 = abstain (dims the control, NOT a quality floor); 1/2/3 map to correctness
// in the DB view, never here. confidence + render→tap latency ride along as
// features only — never folded into correctness.
function RatingControl({ responseId, confidence, renderedAt, onRate }) {
  const [picked, setPicked] = useState(null);
  if (!responseId || typeof onRate !== 'function') return null;
  const rate = (v) => {
    setPicked(v);
    const latencyMs = renderedAt ? Date.now() - renderedAt : null;
    onRate(responseId, 'rating', { value: v, confidence: confidence ?? null, latencyMs });
  };
  const OPTS = [
    { v: 1, glyph: '✗', label: 'Bad' },
    { v: 2, glyph: '~', label: 'Fine' },
    { v: 3, glyph: '✓', label: 'Good' },
    { v: 0, glyph: '·', label: 'Skip' },
  ];
  return (
    <span className={`rating-control${picked === 0 ? ' rating-control--dismissed' : ''}`} role="group" aria-label="Rate this response">
      {OPTS.map((o) => (
        <button
          key={o.v}
          type="button"
          className={`message-action-btn rating-btn rating-btn--${o.label.toLowerCase()}${picked === o.v ? ' rating-btn--picked' : ''}`}
          onClick={() => rate(o.v)}
          title={o.v > 0 ? `${o.label} (${o.v})` : 'Seen — skip rating'}
          aria-label={o.label}
        >
          {o.glyph}
        </button>
      ))}
    </span>
  );
}

function MessageBubble({ message, onRegenerateWithMode, onEditMessage, isLastMessage, pendingActions = [], onApproveAction, onCancelAction }) {
  const { role, content, model, timestamp, detectedMode, userOverride, routingReason, confidence, flag, memoryUsed, isBriefing, briefingType, isStreaming, responseId, renderedAt, liveSources, generatedFiles, isContinuation, steps = [] } = message;
  // thinkingModel already lives on context state (spread into the value
  // object) — reused here as the inline bar's modelHint rather than
  // threading a new prop through MessageList → MessageBubble.
  const { submitFeedback, alwaysAllowTool, allowToolForChat, thinkingModel } = useSolomon();
  const copyFiredRef = useRef(0);
  const isSolomon = role === 'assistant';
  const isUser    = role === 'user';
  const [copied, setCopied] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(false);
  const hasContent = !!content && content.trim().length > 0;

  // Special rendering for daily briefings (morning + evening)
  if (isBriefing) {
    return <BriefingBubble content={content} timestamp={timestamp} briefingType={briefingType || 'morning'} />;
  }

  // Output-quality: a copy is a logged FEATURE (not a label). Debounced 2s per
  // response (§6.3) so button-copy + text-selection-copy don't double-fire.
  const fireCopySignal = () => {
    if (!isSolomon || !responseId) return;
    const now = Date.now();
    if (now - copyFiredRef.current < 2000) return;
    copyFiredRef.current = now;
    submitFeedback(responseId, 'copy');
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
    fireCopySignal();
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Split the response into text vs. visual blocks. Visuals (charts and
  // diagrams) get rendered OUTSIDE the message bubble in a wider container
  // so they're actually readable and interactive — inside the bubble they
  // were capped at ~420px and felt cramped on desktop.
  const { textContent, visuals } = splitTextAndVisuals(content, isStreaming, isSolomon);

  return (
    <div className={`message-row message-row--${isSolomon ? 'solomon' : 'user'} animate-fade-in-up`}>

      {/* Avatar */}
      {isSolomon && (
        <div className="message-avatar message-avatar--solomon">S</div>
      )}

      {/* Stack: bubble (text) + optional full-width visuals below */}
      <div className="message-content-stack">

        {/* Bubble */}
        <div className={`message-bubble message-bubble--${isSolomon ? 'solomon' : 'user'}`}>

          {/* Solomon label + model indicator badge */}
          {isSolomon && (
            <div className="message-header">
              <span className="message-sender">Solomon</span>
              {model && model !== 'error' && (
                <ModelIndicatorBadge
                  model={model}
                  confidence={confidence}
                  routingReason={routingReason}
                />
              )}
              <ConfidenceFlag flag={flag} />
              {memoryUsed && (
                <span
                  className="memory-used-badge"
                  title="Solomon referenced past conversations to answer this"
                >
                  🧠
                </span>
              )}
              {Array.isArray(liveSources) && liveSources.length > 0 && (
                <span
                  className="live-search-badge"
                  title="Solomon searched the web for this answer"
                >
                  Live
                </span>
              )}
            </div>
          )}

          {/* Tool-call steps, collapsed: once the answer has started
              streaming (or finished), the step history folds into a toggle
              strip at the top of the card body instead of a live line. */}
          {isSolomon && hasContent && steps.length > 0 && (
            <div className="message-steps-strip-wrap">
              <button
                type="button"
                className="message-steps-strip"
                aria-expanded={stepsOpen}
                onClick={() => setStepsOpen((o) => !o)}
              >
                <span className="message-steps-strip-label">{summarizeSteps(steps)}</span>
                <span
                  className={`message-steps-chevron${stepsOpen ? ' message-steps-chevron--open' : ''}`}
                  aria-hidden="true"
                >
                  ▾
                </span>
              </button>
              {stepsOpen && (
                <div className="message-steps-list">
                  {steps.map((s, i) => (
                    <div key={i} className="message-step-line">
                      {stepLineText(s, { past: true })}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Content (text only — visuals render below the bubble) */}
          <div className="message-content" onCopy={fireCopySignal}>
            {formatContent(textContent, isSolomon ? liveSources : null)}
          </div>

          {/* Merged progress: step history so far, then the thinking bar —
              only before any content has streamed in. Once content starts,
              the collapsed strip above takes over and this block is gone. */}
          {isSolomon && isStreaming && !hasContent && (
            <div className="message-steps-live">
              {steps.map((s, i) => (
                <div
                  key={i}
                  className={`message-step-line${i === steps.length - 1 ? ' message-step-line--live' : ''}`}
                >
                  {stepLineText(s)}{i === steps.length - 1 ? '…' : ''}
                </div>
              ))}
              <ThinkingIndicator inline modelHint={thinkingModel} />
            </div>
          )}

          {/* Numbered web sources behind [n] citations */}
          {isSolomon && !isStreaming && Array.isArray(liveSources) && liveSources.length > 0 && (
            <MessageSources sources={liveSources} />
          )}

          {/* Generated-file download cards (generate_file tool — ephemeral v1) */}
          {isSolomon && !isStreaming && Array.isArray(generatedFiles) && generatedFiles.length > 0 && (
            <div className="file-card-row">
              {generatedFiles.map((f) => (
                <FileCard key={f.fileId} file={f} />
              ))}
            </div>
          )}

          {/* Footer: timestamp + copy + mode badge */}
          <div className="message-footer">
            <span className="message-time">{formatTime(timestamp)}</span>

            {isSolomon && (
              <div className="message-actions">
                {/* A continuation has no paired user message to regenerate from,
                    and its action is consumed — so the control is absent, not
                    disabled (spec §3.6). live_messages semantics are untouched. */}
                {detectedMode && onRegenerateWithMode && !isContinuation && (
                  <ModeBadge
                    detectedMode={detectedMode}
                    userOverride={userOverride}
                    onSwitch={onRegenerateWithMode}
                    isLastMessage={isLastMessage}
                  />
                )}
                <button
                  className="message-action-btn"
                  onClick={handleCopy}
                  title="Copy message"
                >
                  {copied ? '✓' : '⧉'}
                </button>
                <RatingControl
                  responseId={responseId}
                  confidence={confidence}
                  renderedAt={renderedAt}
                  onRate={submitFeedback}
                />
              </div>
            )}

            {/* User messages: Edit (load text back into the input to resend) +
                Copy. Gated to the user role only — never on Solomon or briefings. */}
            {isUser && (
              <div className="message-actions">
                <button
                  className="message-action-btn"
                  onClick={() => onEditMessage?.(content)}
                  title="Edit message"
                  aria-label="Edit message"
                >
                  <EditIcon />
                </button>
                <button
                  className="message-action-btn"
                  onClick={handleCopy}
                  title="Copy message"
                  aria-label="Copy message"
                >
                  {copied ? '✓' : '⧉'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Full-width visuals container — sits below the bubble */}
        {visuals.length > 0 && (
          <div className="message-visuals">
            {visuals.map((v, i) => {
              if (v.type === 'chart')   return <ChartBlock   key={`v-${i}`} config={v.config} />;
              if (v.type === 'mermaid') return <DiagramBlock key={`v-${i}`} syntax={v.syntax} />;
              if (v.type === 'image')   return <GeneratedImage key={`v-${i}`} id={v.id} />;
              if (v.type === 'pending') return (
                <div key={`v-${i}`} className="message-visual-pending">
                  Preparing {v.kind === 'chart' ? 'chart' : 'diagram'}…
                </div>
              );
              return null;
            })}
          </div>
        )}

        {/* Action cards — shown on the last Solomon message when actions are pending */}
        {isSolomon && isLastMessage && pendingActions.length > 0 && (
          <div className="message-action-cards">
            {pendingActions.map(action => (
              <ActionCard
                key={action.id}
                action={action}
                onApprove={onApproveAction}
                onCancel={onCancelAction}
                onAlwaysAllow={alwaysAllowTool}
                onAllowForChat={allowToolForChat}
              />
            ))}
          </div>
        )}

        {/* Embedded email compose REMOVED 2026-07-28 (D9 Option B).
            It was already unreachable: `emailDraft` was destructured off the
            message here, but nothing in the backend ever emitted it and nothing
            in the frontend ever set it — the branch could never be true. */}

      </div>

      {/* User avatar */}
      {isUser && (
        <div className="message-avatar message-avatar--user">J</div>
      )}
    </div>
  );
}

// Memoized: during streaming, each chunk mutates the active bubble's `message`
// object and produces a new `messages` array, re-rendering the whole list. Without
// memo, every settled bubble also re-runs its full markdown/table/inline parse on
// each chunk (O(n²) as the conversation grows) → choppy, non-smooth rendering. memo
// limits re-renders to bubbles whose props actually changed — i.e. the streaming one.
// Props are primitives + stable callbacks, so the default shallow compare is correct.
export default memo(MessageBubble);

// ============================================================
// CONTENT PARSING — split text from chart/diagram blocks
// ============================================================

/**
 * Walk the raw message and extract chart/mermaid blocks into a separate
 * array. Returns the text-only string (with the visual blocks stripped)
 * plus the ordered list of visuals so the bubble renders text alone and
 * visuals render in a wider sibling container.
 */
function splitTextAndVisuals(content, isStreaming = false, allowImages = false) {
  if (!content) return { textContent: '', visuals: [] };

  const BLOCK_REGEX = /```(chart|mermaid)\s*\n([\s\S]*?)```/g;
  const visuals = [];
  let textContent = '';
  let lastIndex = 0;
  let match;

  while ((match = BLOCK_REGEX.exec(content)) !== null) {
    if (match.index > lastIndex) {
      textContent += content.slice(lastIndex, match.index);
    }

    const blockType = match[1];
    const blockBody = match[2].trim();

    if (blockType === 'chart') {
      try {
        visuals.push({ type: 'chart', config: JSON.parse(blockBody) });
      } catch {
        // Malformed chart JSON — fall back to showing the source as text
        textContent += '```\n' + blockBody + '\n```';
      }
    } else if (blockType === 'mermaid') {
      visuals.push({ type: 'mermaid', syntax: blockBody });
    }

    lastIndex = match.index + match[0].length;
  }

  let remainingText = content.slice(lastIndex);

  // While streaming, an opener like "```chart\n{partial..." won't match
  // the regex above (no closing fence yet) and would otherwise leak raw
  // JSON into the rendered text. Detect it, strip it, and emit a
  // placeholder visual. When the closing fence arrives, the regex above
  // will match the now-complete block and the placeholder vanishes.
  if (isStreaming && remainingText) {
    const PENDING_OPENER = /```(chart|mermaid)\s*\n/;
    const pendingMatch = remainingText.match(PENDING_OPENER);
    if (pendingMatch) {
      visuals.push({ type: 'pending', kind: pendingMatch[1] });
      remainingText = remainingText.slice(0, pendingMatch.index);
    }
  }

  textContent += remainingText;

  // [image:<uuid>] tokens (executor-written assistant messages — spec §3.4):
  // fence-aware and assistant-only — a token quoted in a code block or typed
  // by the user stays literal text (review finding 2026-08-27).
  if (allowImages) {
    const { text: remaining, ids } = extractImageVisuals(textContent);
    for (const id of ids) visuals.push({ type: 'image', id });
    textContent = remaining;
  }

  return { textContent: textContent.trim(), visuals };
}

// ============================================================
// HELPERS
// ============================================================

// Inline pencil icon for the "Edit" affordance on user messages. Sized to
// match the Copy button glyph (no icon dependency in this codebase).
function EditIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function CodeBlock({ lang, code }) {
  const [copyState, setCopyState] = useState(null); // null | 'copied' | 'failed'
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code); // raw code only — never the lang id or the ``` fences
      setCopyState('copied');
    } catch {
      setCopyState('failed'); // clipboard unavailable (e.g. insecure context) — tell the user, don't fail silently
    }
    setTimeout(() => setCopyState(null), 1500);
  };
  return (
    <div className="content-code-block">
      <div className="content-code-header">
        <span className="content-code-lang">{lang || 'text'}</span>
        <button type="button" className="content-code-copy" onClick={handleCopy} aria-label="Copy code">
          {copyState === 'copied' ? 'Copied' : copyState === 'failed' ? 'Failed' : 'Copy'}
        </button>
      </div>
      <pre className="content-code-pre"><code>{code}</code></pre>
    </div>
  );
}

function formatContent(content, liveSources = null) {
  if (!content) return null;

  // Citation map for live-web answers: [n] tokens resolve to source URLs.
  // Null when the message has no live sources — [n] then renders literally.
  const cite = Array.isArray(liveSources) && liveSources.length > 0
    ? new Map(liveSources.map((s) => [s.id, s]))
    : null;

  // Split content on block-level constructs in ONE pass so order is preserved:
  //   - fenced blocks: ```chart / ```mermaid / ```lang
  //   - block math:    $$...$$  and  \[...\]
  // A single alternation regex keeps charts, code, and math interleaved
  // correctly. The fenced-block alternative is listed first so a ``` body
  // that happens to contain `$$` is consumed as code, not split as math.
  // NOTE: bare single `$` is deliberately NOT a *block* delimiter (currency-
  // safe); only the doubled `$$` (and `\[ \]`) open block math. Single-$ spans
  // that provably contain a LaTeX command (\times, \mathbf, …) are handled
  // downstream in renderInline — see the command-gated alternative there.
  const BLOCK_REGEX = /```(\w*)\s*\n([\s\S]*?)```|\$\$([\s\S]+?)\$\$|\\\[([\s\S]+?)\\\]/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = BLOCK_REGEX.exec(content)) !== null) {
    // Text before the block
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index) });
    }

    const isFence = match[1] !== undefined; // fenced-block alternative matched

    if (!isFence) {
      // Block math: $$...$$ (group 3) or \[...\] (group 4).
      const tex = (match[3] !== undefined ? match[3] : match[4]).trim();
      parts.push({ type: 'mathblock', tex });
      lastIndex = match.index + match[0].length;
      continue;
    }

    const blockType = match[1]; // 'chart', 'mermaid', or a language id ('js', '', etc.)
    const blockBody = match[2].trim();

    if (blockType === 'chart') {
      try {
        const chartConfig = JSON.parse(blockBody);
        parts.push({ type: 'chart', config: chartConfig });
      } catch {
        parts.push({ type: 'text', content: '```\n' + blockBody + '\n```' });
      }
    } else if (blockType === 'mermaid') {
      parts.push({ type: 'mermaid', syntax: blockBody });
    } else {
      // any other fence (```js, ```python, ``` ) → styled code block.
      // Use match[2] with only a trailing-newline strip so leading
      // indentation on the first line is preserved for code.
      parts.push({ type: 'code', lang: blockType, code: match[2].replace(/\n$/, '') });
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text after last block
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex) });
  }

  // If no special blocks found, just format as text
  if (parts.length === 0) {
    parts.push({ type: 'text', content });
  }

  return parts.map((part, partIdx) => {
    if (part.type === 'chart') {
      return <ChartBlock key={`chart-${partIdx}`} config={part.config} />;
    }
    if (part.type === 'mermaid') {
      return <DiagramBlock key={`diagram-${partIdx}`} syntax={part.syntax} />;
    }
    if (part.type === 'code') {
      return <CodeBlock key={`code-${partIdx}`} lang={part.lang} code={part.code} />;
    }
    if (part.type === 'mathblock') {
      const html = renderMathToHtml(part.tex, true);
      if (html == null) {
        // KaTeX failed even with throwOnError:false — degrade to plain-text
        // math (never raw tex) so a bad formula stays readable.
        return <div key={`math-${partIdx}`} className="content-math-block">{latexToPlainText(part.tex)}</div>;
      }
      return (
        <div
          key={`math-${partIdx}`}
          className="content-math-block"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    // Render text lines. Walk the array (not a plain .map) so a
    // header+separator+body run can be consumed as a single table element
    // while every other line keeps its exact existing rendering.
    const lines = part.content.split('\n');
    const rendered = [];
    let i = 0;
    while (i < lines.length) {
      // Markdown table detection: header + separator + ≥1 body rows.
      const parsed = parseTableAt(lines, i);
      if (parsed) {
        rendered.push(
          renderTable(parsed.table, `table-${partIdx}-${i}`, cite)
        );
        i = parsed.nextIndex;
        continue;
      }

      const line = lines[i];
      const key = `${partIdx}-${i}`;
      // Horizontal rule: a line that is exactly 3+ of `-`, `*`, or `_`
      // (trimmed, nothing else). Tested BEFORE blockquote/heading/list.
      // A markdown-table separator (`| --- |`) contains pipes and is consumed
      // by parseTableAt above, so a bare `---` reaching here is a real HR.
      if (/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
        rendered.push(<hr key={key} className="content-hr" />);
      }
      // Blockquote: a line starting with `> `. Consecutive quote lines each
      // render as their own blockquote element (no merging — kept simple).
      else if (line.startsWith('> ')) {
        rendered.push(<blockquote key={key} className="content-blockquote">{renderInline(line.slice(2), cite)}</blockquote>);
      }
      // Bold line (entire line is **bold**)
      else if (/^\*\*[^*]+\*\*$/.test(line.trim())) {
        rendered.push(<p key={key} className="content-bold">{line.trim().slice(2, -2)}</p>);
      }
      // Heading — ATX levels 1-6. This ladder implemented only `# ` and `## `,
      // so `### ` and deeper fell through to the paragraph branch below and
      // printed their hashes verbatim to the reader (operator-reported
      // 2026-08-27 on a `### 1. …` heading). The depth picks the class; levels
      // 1 and 2 keep their exact prior markup, so nothing that already rendered
      // changes. Heading text still bypasses renderInline — inline marks inside
      // a heading render literally, as they always have; that is a separate gap.
      else if (/^#{1,6}\s+/.test(line)) {
        const level = line.match(/^#+/)[0].length;
        const cls = level >= 2 ? `content-heading content-h${level}` : 'content-heading';
        rendered.push(<h4 key={key} className={cls}>{line.replace(/^#{1,6}\s+/, '')}</h4>);
      }
      // List item
      else if (line.startsWith('- ') || line.startsWith('• ')) {
        rendered.push(<li key={key} className="content-list-item">{renderInline(line.slice(2), cite)}</li>);
      }
      else if (/^\d+\.\s/.test(line)) {
        rendered.push(<li key={key} className="content-list-item content-list-item--numbered">{renderInline(line.replace(/^\d+\.\s/, ''), cite)}</li>);
      }
      // Empty line
      else if (line.trim() === '') {
        rendered.push(<br key={key} />);
      }
      else {
        rendered.push(<p key={key} className="content-paragraph">{renderInline(line, cite)}</p>);
      }
      i += 1;
    }
    return rendered;
  });
}

// ============================================================
// MARKDOWN TABLE PARSING + RENDERING
// ============================================================

// A separator line: cells of ≥3 dashes with optional leading/trailing ':'
// for alignment, pipe-delimited. e.g. `| :--- | :--: | ---: |` or `--- | ---`
const TABLE_SEPARATOR_REGEX = /^\s*\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?\s*$/;

/**
 * Split a table row into trimmed cells: strip ONE optional leading and ONE
 * optional trailing pipe, then split on the remaining pipes.
 */
function splitTableRow(line) {
  let s = line.trim();
  if (s.startsWith('|')) s = s.slice(1);
  if (s.endsWith('|'))   s = s.slice(0, -1);
  return s.split('|').map(c => c.trim());
}

/**
 * Derive per-column alignment from the separator cells.
 * `:--` → 'left', `:--:` → 'center', `--:` → 'right', else null.
 */
function parseAlignment(sepCells) {
  return sepCells.map(cell => {
    const c = cell.trim();
    const left  = c.startsWith(':');
    const right = c.endsWith(':');
    if (left && right) return 'center';
    if (right)         return 'right';
    if (left)          return 'left';
    return null;
  });
}

/**
 * Detect a Markdown table starting at lines[i]. Requires:
 *  - a header line containing '|'
 *  - immediately followed by a valid separator line
 *  - header column count === separator column count
 *  - ≥1 following body lines containing '|'
 * Returns { table: { header, align, rows }, nextIndex } or null.
 * The strict separator requirement is what prevents a stray '|' in prose
 * from false-positiving as a table.
 */
function parseTableAt(lines, i) {
  const headerLine = lines[i];
  const sepLine = lines[i + 1];
  if (headerLine == null || sepLine == null) return null;
  if (!headerLine.includes('|')) return null;
  if (!TABLE_SEPARATOR_REGEX.test(sepLine)) return null;

  const header   = splitTableRow(headerLine);
  const sepCells = splitTableRow(sepLine);
  if (header.length !== sepCells.length) return null;

  const align = parseAlignment(sepCells);

  // Consume contiguous body lines that still contain a pipe.
  const rows = [];
  let j = i + 2;
  while (j < lines.length && lines[j].includes('|')) {
    rows.push(splitTableRow(lines[j]));
    j += 1;
  }
  if (rows.length === 0) return null; // header+separator with no body → not a table

  return { table: { header, align, rows }, nextIndex: j };
}

function renderTable(table, key, cite = null) {
  const { header, align, rows } = table;
  return (
    <div className="content-table-wrap" key={key}>
      <table className="content-table">
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} style={align[i] ? { textAlign: align[i] } : undefined}>{renderInline(h, cite)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {header.map((_, ci) => (
                <td key={ci} style={align[ci] ? { textAlign: align[ci] } : undefined}>{renderInline(r[ci] ?? '', cite)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Inline-token splitter. Order in the alternation breaks TIES AT THE SAME START
// INDEX — JS alternation is leftmost-match-wins, so an earlier-STARTING span of
// any kind wins outright regardless of this order (a `$…\to…$` span opening
// before a backtick will swallow the code span; pre-existing, and reachable
// since the command set widened):
//   1. `code`            — beats a URL/`**` that starts at the same index
//   2. \(...\)           — inline math (escaped parens)
//   3. $…$ w/ command    — single-$ inline math, ONLY when the span contains a
//                          LaTeX command (\times, \rightarrow, \alpha, …) AND
//                          the opening $ is not followed by a digit — money
//                          always opens "$<digit>", so `$170,000`, ranges, and
//                          even a bare \times BETWEEN two amounts never match.
//                          IMPORTED from utils/latexNormalize.js, never
//                          restated: this pattern used to be a hand-copied
//                          third home for the command list, so extending the
//                          list left the splitter behind and the span reached
//                          the reader raw (the 2026-08-31 `$\rightarrow$`).
//   4. [text](url)       — markdown link
//   5. [n]               — live-web citation marker (only links when the
//                          message carries a matching live source; literal otherwise)
//   6. **bold**          — bold (inner content re-runs renderInline, so math
//                          inside bold renders — the 2026-07-16 offender shape)
//   7. ~~strike~~        — strikethrough (inner re-runs renderInline)
//   8. bare http(s) URL  — autolink, last so it can't swallow a [](...) url
// The link/bare-URL alternatives are constrained to http(s) at the regex
// level; `javascript:` / `data:` etc. never match and render as plain text.
// The citation alternative sits AFTER the markdown link so `[1](url)` is
// consumed as a link, never as a citation.
// Alternatives stay individually readable as literals; alternative 3 is the
// imported source string. Order within the array IS the precedence above.
const INLINE_ALTERNATIVES = [
  /`[^`]+`/,                              // 1. inline code
  /\\\([^)]*?\\\)/,                       // 2. \(…\) inline math
  DOLLAR_LATEX_SPAN_SOURCE,               // 3. $…command…$ (imported)
  /\[[^\]]+\]\((?:https?:\/\/)[^)\s]+\)/, // 4. [text](url)
  /\[\d{1,2}\]/,                          // 5. [n] citation
  /\*\*[^*]+\*\*/,                        // 6. **bold**
  /~~[^~]+~~/,                            // 7. ~~strike~~
  /https?:\/\/[^\s<>()]+/,                // 8. bare http(s) URL
];

const INLINE_SPLIT_REGEX = new RegExp(
  `(${INLINE_ALTERNATIVES.map((a) => (typeof a === 'string' ? a : a.source)).join('|')})`,
  'g',
);

// Pull (text, url) out of a markdown link token whose url is already known to
// be http(s) (the split regex only matched http(s) links). Returns null if the
// shape doesn't parse.
function parseMarkdownLink(token) {
  const m = token.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)$/);
  if (!m) return null;
  return { label: m[1], url: m[2] };
}

function renderInline(text, cite = null) {
  if (!text) return text;
  const parts = text.split(INLINE_SPLIT_REGEX);
  return parts.map((part, i) => {
    if (!part) return part;
    // Inline code — checked first so its contents stay literal.
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="content-code-inline">{part.slice(1, -1)}</code>;
    }
    // Live-web citation [n] — superscript pill linking to the source. Only
    // when this message carries a matching live source; otherwise literal
    // (so "[1]" in ordinary prose is untouched).
    const citeMatch = part.match(/^\[(\d{1,2})\]$/);
    if (citeMatch) {
      const src = cite?.get(parseInt(citeMatch[1], 10));
      if (src) {
        // Research findings may carry no URL — render the pill without a link.
        if (!src.url) {
          return (
            <span key={i} className="citation-pill" title={src.title}>
              <sup>{citeMatch[1]}</sup>
            </span>
          );
        }
        return (
          <a
            key={i}
            className="citation-pill"
            href={src.url}
            target="_blank"
            rel="noopener noreferrer"
            title={src.title}
          >
            <sup>{citeMatch[1]}</sup>
          </a>
        );
      }
      return part;
    }
    // Inline math \(...\)
    if (part.startsWith('\\(') && part.endsWith('\\)')) {
      const tex = part.slice(2, -2).trim();
      const html = renderMathToHtml(tex, false);
      if (html == null) return latexToPlainText(tex); // degrade to plain text
      return (
        <span
          key={i}
          className="content-math-inline"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    // Single-$ inline math — STRICTLY command-gated (containsLatexCommand, the
    // singular): only spans carrying a backslash command reach KaTeX. Command-free
    // identifier spans ($T$, $P_{max}$) fall through to the plain-text fallback
    // below, so the Issue 98 identifier admission never widens what is rendered
    // as HTML. Never money either way (see splitter comment).
    if (part.startsWith('$') && part.endsWith('$') && containsLatexCommand(part)) {
      const tex = part.slice(1, -1).trim();
      const html = renderMathToHtml(tex, false);
      if (html == null) return latexToPlainText(part); // degrade to plain text
      return (
        <span
          key={i}
          className="content-math-inline"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }
    // Markdown link [text](url) — url guaranteed http(s) by the split regex.
    if (part.startsWith('[')) {
      const link = parseMarkdownLink(part);
      if (link) {
        return (
          <a key={i} className="content-link" href={link.url} target="_blank" rel="noopener noreferrer">
            {link.label}
          </a>
        );
      }
      return part; // shape didn't parse — render literally
    }
    // Bold — inner content re-runs renderInline so inline math (incl. the
    // command-gated $…$ path) renders inside bold. Safe: [^*]+ in the split
    // regex means the inner text can never contain another bold token.
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{renderInline(part.slice(2, -2), cite)}</strong>;
    }
    // Strikethrough — same inner re-run as bold.
    if (part.startsWith('~~') && part.endsWith('~~')) {
      return <del key={i}>{renderInline(part.slice(2, -2), cite)}</del>;
    }
    // Bare http(s) URL — autolinked. Only http/https reach here (regex-gated),
    // so javascript:/data: schemes are never linkified.
    if (/^https?:\/\//.test(part)) {
      return (
        <a key={i} className="content-link" href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      );
    }
    // Plain text — convert bare LaTeX commands the splitter didn't capture
    // (e.g. "\times" between two dollar amounts, which the money gate keeps
    // out of the $-span path). Inline code was already split out above, so
    // this can never touch code; no-op when no command is present.
    return containsLatexCommands(part) ? latexToPlainText(part) : part;
  });
}
